import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, DollarSign, Box, Tag, Search, ShoppingCart as ShoppingCartIcon, AlertCircle, RefreshCw } from 'lucide-react';
import { useCart } from '../components/CartContext';

// Datos de ejemplo como fallback (si la API falla)
const mockProducts = [
  {
    codArticulo: 'ART001',
    desMarca: 'Nike',
    desClase: 'Deportiva',
    desArticulo: 'Zapatillas Air Max',
    stock: 25,
    precio: 250.00
  },
  {
    codArticulo: 'ART002',
    desMarca: 'Adidas',
    desClase: 'Casual',
    desArticulo: 'Camiseta Stan Smith',
    stock: 15,
    precio: 120.00
  },
  {
    codArticulo: 'ART003',
    desMarca: 'Puma',
    desClase: 'Running',
    desArticulo: 'Pantalones Fitness',
    stock: 8,
    precio: 180.00
  }
];

const Stock = ({ currentUser, token }) => { // Recibe currentUser y token como props
  const [products, setProducts] = useState([]); // Lista de productos de la API o mock
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false); // Flag para saber si usa mock
  const { addToCart } = useCart(); // Hook del carrito

  // URL de tu API para obtener productos (¡CÁMBIALA por la tuya!)
  const API_PRODUCTS_URL = 'https://appwebtienda-a6b3ecgnggbjh6c7.centralus-01.azurewebsites.net/api/producto/lista'; // Placeholder - reemplaza con tu endpoint

  // Imagen por defecto (URL externa que siempre carga)
  const DEFAULT_IMAGE_URL = 'https://previews.123rf.com/images/tkacchuk/tkacchuk2004/tkacchuk200400017/143745488-no-picture-icon-editable-line-vector-no-image-no-photo-available-or-no-picture-for-your-website.jpg';

  const fetchProducts = async () => {
    if (!currentUser || !currentUser.idCliente || !token) {
      setError('No se pudo cargar el stock. Verifica tu sesión.');
      setLoading(false);
      loadFallbackData(); // Carga mock si no hay user/token
      return;
    }

    setLoading(true);
    setError('');
    setUsingFallback(false);

    try {
      const response = await fetch(`${API_PRODUCTS_URL}?idCliente=${currentUser.idCliente}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // Token en header para autenticación
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.response && Array.isArray(data.response)) {
          // Mapea los campos de la API (data.response) al formato del componente
          const mappedProducts = data.response.map(product => ({
            id: product.codArticulo,
            name: product.desArticulo || '',
            brand: product.desMarca || '',
            category: `${product.desMarca || ''} - ${product.desClase || ''}`.trim(), // Combina marca y clase, maneja nulls
            sku: product.codArticulo, // Puede ser número o string
            stock: product.stock || 0,
            price: parseFloat(product.precio) || 0,
            imageUrl: product.imageUrl || DEFAULT_IMAGE_URL, // Usa imagen por defecto si no hay URL
          }));
          setProducts(mappedProducts);
        } else {
          throw new Error('Respuesta inválida de la API (response no es array)');
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al cargar productos:', err);
      setError(`No se pudieron cargar los productos: ${err.message}. Usando datos de ejemplo.`);
      setUsingFallback(true);
      loadFallbackData(); // Carga mock como fallback
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = () => {
    // Mapea mock data al formato del componente
    const mappedMock = mockProducts.map(product => ({
      id: product.codArticulo,
      name: product.desArticulo,
      brand: product.desMarca,
      category: `${product.desMarca} - ${product.desClase}`,
      sku: product.codArticulo,
      stock: product.stock,
      price: product.precio,
      imageUrl: DEFAULT_IMAGE_URL, // Usa imagen por defecto
    }));
    setProducts(mappedMock);
  };

  useEffect(() => {
    fetchProducts();
  }, [currentUser, token]); // Recarga si cambian user o token

  // Filtra productos localmente por búsqueda (convierte a string para evitar errores de tipo)
  const filteredProducts = products.filter(product => {
    const searchLower = searchTerm.toLowerCase();
    const productName = String(product.name || '').toLowerCase();
    const productCategory = String(product.category || '').toLowerCase();
    const productSku = String(product.sku || '').toLowerCase(); // Convierte sku a string

    return (
      productName.includes(searchLower) ||
      productCategory.includes(searchLower) ||
      productSku.includes(searchLower)
    );
  });

  const handleRetry = () => {
    setError('');
    fetchProducts();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Cargando inventario...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.h1
        className="text-4xl font-extrabold text-gray-900 mb-8 text-center bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Inventario de Productos
      </motion.h1>

      {/* Mensaje de error con botón de retry si hay problemas */}
      {error && (
        <motion.div
          className="mb-6 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-xl flex items-center gap-2 justify-between"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            {usingFallback && <span className="text-sm font-medium">(Usando datos de ejemplo)</span>}
          </div>
          {!usingFallback && (
            <motion.button
              onClick={handleRetry}
              className="px-3 py-1 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Reintentar
            </motion.button>
          )}
        </motion.div>
      )}

      <motion.div
        className="mb-8 bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 shadow-lg flex items-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Search className="w-6 h-6 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar producto por nombre, marca, clase o código..."
          className="flex-1 p-2 bg-transparent outline-none text-gray-800 placeholder-gray-400"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading}
        />
      </motion.div>

      {products.length === 0 && !loading && !searchTerm && (
        <motion.div
          className="text-center py-12 text-gray-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-xl font-medium">No hay productos disponibles en tu inventario.</p>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <img src={product.imageUrl} alt={product.name} className="w-32 h-32 object-cover rounded-lg mb-4 shadow-md" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{product.name}</h2>
              <div className="space-y-2 text-gray-600 mb-4">
                <p className="flex items-center justify-center gap-2"><Tag className="w-4 h-4 text-purple-500" /> Categoría: {product.category}</p>
                <p className="flex items-center justify-center gap-2"><Box className="w-4 h-4 text-blue-500" /> Código: {product.sku}</p>
                <p className="flex items-center justify-center gap-2"><DollarSign className="w-4 h-4 text-green-500" /> Precio: PEN {product.price.toFixed(2)}</p>
              </div>
              <div className={`px-4 py-2 rounded-full font-semibold text-lg ${
                product.stock > 50 ? 'bg-green-100 text-green-800' :
                product.stock > 10 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                Stock: {product.stock} unidades
              </div>
              {product.stock > 0 ? (
                <motion.button
                  onClick={() => addToCart(product)}
                  className="mt-4 flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ShoppingCartIcon className="w-5 h-5" />
                  Añadir al Carrito
                </motion.button>
              ) : (
                <p className="mt-4 text-red-500 font-semibold">Agotado</p>
              )}
            </motion.div>
          ))
        ) : (
          searchTerm ? (
            <motion.div
              className="col-span-full bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-8 text-center shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <p className="text-xl text-gray-600">No se encontraron productos con ese criterio de búsqueda.</p>
            </motion.div>
          ) : null
        )}
      </div>
    </div>
  );
};

export default Stock;